import { useCallback, useEffect, useState } from "react";
import { Box, Input, Text } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import { useDebouncedCallback } from "#app/lib/hooks/use-debounced-callback";

// Input is always w-full, so the width token constrains the wrapper instead.
// `full` is the browser's own intrinsic field width (the wrapper's 100% has no
// definite parent to resolve against), so a group that must align a field with
// a control the browser does NOT size that way passes an explicit number.
const WIDTH_PX = {
  xs: 64,
  sm: 96,
  md: 128,
  lg: 160,
  xl: 192,
  full: "100%",
} as const;

export function EditableField({
  fieldId,
  label,
  initialValue,
  disabled,
  onSave,
  placeholder,
  width = "full",
  validate,
  errorMessage,
  error,
  sanitize,
}: Omittable<{
  fieldId: string;
  /** Accessible name for the input. The surrounding `SettingsGroupRow` label
   *  is visual-only (no `htmlFor` association), so without this the field is
   *  anonymous to a screen reader. */
  label: string;
  initialValue: string;
  disabled?: boolean;
  onSave: (fieldId: string, value: string) => void;
  placeholder?: string;
  width?: keyof typeof WIDTH_PX | number;
  validate?: (value: string) => boolean;
  /** Shown once the field is left holding a value `validate` refuses. A
   *  function words the refusal for the value itself ("Name is required"
   *  for a blank, a length rule otherwise). */
  errorMessage?: string | ((value: string) => string);
  /** A refusal from the save itself (the server's reason), shown under the
   *  field until the host clears it. */
  error?: string | undefined;
  /** Normalizes every value before it enters state (typing and paste alike).
   *  A typed character the sanitizer would drop entirely is blocked at
   *  keydown, so the caret never jumps. A character it REWRITES (a space the
   *  sanitizer turns into a hyphen) is let through — the rewrite keeps the
   *  length, so the caret stays put and the substitution is the point. */
  sanitize?: (value: string) => string;
}>) {
  const [value, setValue] = useState(initialValue);
  const [invalid, setInvalid] = useState(false);
  const {
    debounced: debouncedSave,
    cancel: cancelSave,
    flush: flushSave,
  } = useDebouncedCallback(onSave, 300);

  useEffect(() => {
    setValue(initialValue);
    setInvalid(false);
  }, [initialValue]);

  const handleChange = useCallback(
    (raw: string) => {
      const text = sanitize ? sanitize(raw) : raw;
      setValue(text);
      setInvalid(false);
      if (!disabled) {
        const isValid = !validate || validate(text);
        if (isValid) {
          debouncedSave(fieldId, text);
        } else {
          cancelSave();
        }
      }
    },
    [fieldId, disabled, debouncedSave, cancelSave, validate, sanitize],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (sanitize && e.key.length === 1 && sanitize(e.key) === "") {
        e.preventDefault();
      }
    },
    [sanitize],
  );

  // Leaving the field saves at once, so a reload or a new page right after
  // does not drop the edit.
  const handleBlur = useCallback(() => {
    flushSave();
    if (validate && !validate(value)) {
      setInvalid(true);
    }
  }, [flushSave, validate, value]);

  return (
    <Box
      direction="column"
      gap="xs"
      width={typeof width === "number" ? width : WIDTH_PX[width]}
    >
      <Input
        aria-label={label}
        aria-invalid={invalid || !!error || undefined}
        disabled={disabled}
        onBlur={handleBlur}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        type="text"
        value={value}
      />
      {invalid && errorMessage ? (
        <Text size="sm" variant="destructive">
          {typeof errorMessage === "function" ? errorMessage(value) : errorMessage}
        </Text>
      ) : error ? (
        <Text size="sm" variant="destructive">{error}</Text>
      ) : null}
    </Box>
  );
}
